import { Toggle, useToast } from "@design";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { chatGetSessionSharingQueryKey, errorMessage, setSessionSpaceShareMutation } from "@/api";
import { Text } from "@/components/Text";

/** Share the chat with everyone in its space (read-only; members can fork it). */
export function ShareWithSpace({
  sessionUuid,
  spaceName,
  shared,
}: Readonly<{ sessionUuid: string; spaceName: string; shared: boolean }>) {
  const { t } = useTranslation("chat", { keyPrefix: "share" });
  const toast = useToast();
  const queryClient = useQueryClient();
  const path = { session_uuid: sessionUuid };
  const update = useMutation({
    ...setSessionSpaceShareMutation(),
    onSuccess: (_data, vars) =>
      toast.success(t(vars.body.shared ? "spaceShared" : "spaceUnshared", { space: spaceName })),
    onError: (error) => toast.error(errorMessage(error, t("failed"))),
    onSettled: () => queryClient.invalidateQueries({ queryKey: chatGetSessionSharingQueryKey({ path }) }),
  });
  return (
    <section className="space-y-2">
      <Text variant="h3" as="h3">
        {t("spaceHeading")}
      </Text>
      <Toggle
        checked={shared}
        disabled={update.isPending}
        label={t("spaceToggle", { space: spaceName })}
        onChange={(checked) => update.mutate({ path, body: { shared: checked } })}
      />
      <Text tone="secondary">{t("spaceHelp")}</Text>
    </section>
  );
}
